import { Directive, OnInit, ViewChild, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSort } from '@angular/material/sort';
import {
  PropertyAdminFilterKey,
  PropertyDocumentStatus,
  PropertyDto,
  PropertyOwnerFilterKey,
  PropertyType,
  PropertyVerificationStatus,
} from '@workspace/data-types';
import {
  ActionButton,
  ActionMenu,
  BaseDataTableComponent,
  DataHeaderFilter,
  DataListColumn,
} from '@workspace/ng-aura';
import { Observable, switchMap, tap } from 'rxjs';
import { EtxeEnumsNiceLabelUtils } from '../../../utils/etxe-enums-nice-label.utils';

export type PropertyColumnKey =
  | 'annonce'
  | 'owner'
  | 'definition.reference'
  | 'definition.propertyType'
  | 'definition.offerType'
  | 'keyInfo.surface.habitable'
  | 'financials.sellingPriceCents'
  | 'marketStatus.state'
  | 'characteristics.interior.roomCount'
  | 'characteristics.interior.bedroomCount'
  | 'dpe.energyClass'
  | 'location.address.city'
  | 'location.address.region'
  | 'verification'
  | 'verification.owner'
  | 'documents.status'
  | 'moderation.hasContactFlag'
  | 'state.status'
  | 'state.firstActivatedAt'
  | 'updatedAt'
  | 'createdAt';

@Directive()
export abstract class AbstractPropertiesDataTableCoreComponent<T extends PropertyDto>
  extends BaseDataTableComponent<T, PropertyColumnKey>
  implements OnInit
{
  override topMenu = signal<ActionMenu | null>(null);
  override columns = signal<DataListColumn<PropertyColumnKey>[]>([
    { key: 'annonce', label: 'Annonce', display: true },
    { key: 'definition.reference', label: 'Référence', display: true },
    { key: 'definition.propertyType', label: 'Type', display: true },
    { key: 'keyInfo.surface.habitable', label: 'Superficie', display: true },
    { key: 'financials.sellingPriceCents', label: 'Prix', display: true },
    { key: 'location.address.region', label: 'Région', display: true },
    { key: 'verification', label: 'Vérifications', display: true },
    { key: 'state.status', label: 'Statut annonce', display: true },
    { key: 'marketStatus.state', label: 'Statut marché', display: false },
    { key: 'characteristics.interior.roomCount', label: 'Pièces', display: false },
    { key: 'dpe.energyClass', label: 'DPE', display: false },
    { key: 'location.address.city', label: 'Ville', display: false },
    { key: 'definition.offerType', label: "Type d'offre", display: false },
    { key: 'updatedAt', label: 'Date de modification', display: false },
    { key: 'createdAt', label: 'Date de création', display: false },
  ]);
  override topButtons = signal<ActionButton[]>([]);
  override itemActions = signal<ActionButton[]>([
    {
      label: 'Ouvrir',
      icon: 'open_in_new',
      onClick: (property: PropertyDto) => this.onItemClicked(property as T),
      disabled: false,
      color: 'primary',
    },
  ]);

  override filters = signal<DataHeaderFilter<PropertyColumnKey>[]>([
    { column: PropertyOwnerFilterKey.Type, label: 'Type' },
    { column: PropertyOwnerFilterKey.Location, label: 'Région' },
    { column: PropertyOwnerFilterKey.State, label: 'Statut' },
  ]);

  @ViewChild(MatSort) sort!: MatSort;

  ngOnInit(): void {
    this.route.queryParamMap
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((params) => {
          const location = params.getAll(PropertyOwnerFilterKey.Location);
          const state = params.getAll(PropertyOwnerFilterKey.State);
          const type = params.getAll(PropertyOwnerFilterKey.Type);
          const verificationOwner = params.getAll(PropertyAdminFilterKey.VerificationOwner);
          const documentStatus = params.getAll(PropertyAdminFilterKey.DocumentStatus);
          const moderationFlag = params.getAll(PropertyAdminFilterKey.ModerationFlag);

          this.filterChanged({
            changed: true,
            filter: {
              [PropertyOwnerFilterKey.Location]: location.length ? location : [],
              [PropertyOwnerFilterKey.State]: state.length ? state : [],
              [PropertyOwnerFilterKey.Type]: type.length ? type : [],
              [PropertyAdminFilterKey.VerificationOwner]: verificationOwner.length ? verificationOwner : [],
              [PropertyAdminFilterKey.DocumentStatus]: documentStatus.length ? documentStatus : [],
              [PropertyAdminFilterKey.ModerationFlag]: moderationFlag.length ? moderationFlag : [],
            },
          });
        }),
        switchMap(() => this.refresh()),
      )
      .subscribe();
  }

  onItemClicked(property: T, auxClick?: boolean): void {
    if (auxClick) {
      window.open(`/properties/${property._id}`, '_blank');
    } else {
      void this.router.navigate(['/properties', property._id]);
    }
  }

  getPropertyTypeLabel(type: string | undefined): string {
    if (!type) return 'Logement';
    const labels: Record<string, string> = {
      apartment: 'Logement',
      house: 'Maison',
      land: 'Terrain',
      parking_box: 'Parking / Box',
      business: 'Fond de commerce',
      professional: 'Immobilier pro',
    };
    return labels[type] || 'Logement';
  }

  abstract override getAll(): Observable<T[]>;
  abstract override countAll(): Observable<number>;

  override getDisplayForEntry(key: PropertyColumnKey, rawValue: string): string {
    if (key === PropertyAdminFilterKey.VerificationOwner) {
      return EtxeEnumsNiceLabelUtils.verificationStatus(rawValue as PropertyVerificationStatus);
    }
    if (key === PropertyAdminFilterKey.DocumentStatus) {
      return EtxeEnumsNiceLabelUtils.documentStatus(rawValue as PropertyDocumentStatus);
    }
    if (key === PropertyAdminFilterKey.ModerationFlag) {
      return rawValue === 'true' ? 'Avec signalement' : 'Sans signalement';
    }
    if (key === PropertyOwnerFilterKey.Type) {
      return EtxeEnumsNiceLabelUtils.propertyType(rawValue as PropertyType);
    }
    return rawValue; // Default display
  }
}
